import React, { useState, useEffect } from 'react'
import { BubbleMenu, Editor } from '@tiptap/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Maximize2,
  Trash2,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'

interface ImageBubbleMenuProps {
  editor: Editor | null
}

const WIDTH_PRESETS = [
  { label: '25%', value: '25%' },
  { label: '50%', value: '50%' },
  { label: '75%', value: '75%' },
  { label: '100%', value: '100%' },
]

export const ImageBubbleMenu: React.FC<ImageBubbleMenuProps> = ({ editor }) => {
  const [customWidth, setCustomWidth] = useState('')
  const [popoverOpen, setPopoverOpen] = useState(false)

  // Update custom width when image is selected
  // Must be called before any early returns (Rules of Hooks)
  useEffect(() => {
    if (editor?.isActive('image')) {
      const attrs = editor.getAttributes('image')
      if (attrs.width) {
        setCustomWidth(attrs.width.toString())
      }
    }
  }, [editor, editor?.state.selection])

  if (!editor) {
    return null
  }

  const shouldShow = ({ editor }: { editor: Editor }) => {
    return editor.isActive('image')
  }

  const handleWidthPreset = (width: string) => {
    editor.chain().focus().updateImage({ width, height: null }).run()
  }

  const handleCustomWidth = () => {
    if (!customWidth) return

    // Add px or % if not specified
    let width = customWidth
    if (!width.includes('%') && !width.includes('px')) {
      width = `${width}px`
    }

    editor.chain().focus().updateImage({ width, height: null }).run()
    setPopoverOpen(false)
    setCustomWidth('')
  }

  const handleAlignment = (align: 'left' | 'center' | 'right') => {
    editor.chain().focus().setTextAlign(align).run()
  }

  const handleRemove = () => {
    editor.chain().focus().deleteSelection().run()
  }

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{
        duration: 100,
        placement: 'top',
        maxWidth: 'none',
      }}
      shouldShow={shouldShow}
      className="bg-card border border-border rounded-lg shadow-lg p-2 flex items-center gap-1 z-[100]"
    >
      <div className="flex items-center gap-1">
        {WIDTH_PRESETS.map((preset) => (
          <Button
            key={preset.value}
            variant={
              editor.getAttributes('image').width === preset.value
                ? 'default'
                : 'ghost'
            }
            size="sm"
            onClick={() => handleWidthPreset(preset.value)}
            className="h-8 px-2 text-xs"
            title={`Set width to ${preset.label}`}
          >
            {preset.label}
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="h-6 mx-1" />

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            title="Custom width"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-64 z-[10000]"
          align="start"
          side="bottom"
          sideOffset={8}
        >
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="custom-width" className="text-sm font-medium">
                Custom Width
              </Label>
              <Input
                id="custom-width"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                placeholder="400px or 80%"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleCustomWidth()
                  }
                }}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Enter a value with px or % (e.g., 400px, 80%)
              </p>
            </div>
            <Button size="sm" onClick={handleCustomWidth} className="w-full">
              Apply Width
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Separator orientation="vertical" className="h-6 mx-1" />

      <Button
        variant={
          editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'
        }
        size="sm"
        onClick={() => handleAlignment('left')}
        className="h-8 w-8 p-0"
        title="Align Left"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        variant={
          editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'
        }
        size="sm"
        onClick={() => handleAlignment('center')}
        className="h-8 w-8 p-0"
        title="Align Center"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        variant={
          editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'
        }
        size="sm"
        onClick={() => handleAlignment('right')}
        className="h-8 w-8 p-0"
        title="Align Right"
      >
        <AlignRight className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-6 mx-1" />

      <Button
        variant="ghost"
        size="sm"
        onClick={handleRemove}
        className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
        title="Remove Image"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </BubbleMenu>
  )
}
